import React from 'react';
import { InputNumber, Radio, Select, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatUnit } from '../../utils/units';
import { formatWarehouseQuantity } from './warehouseQuantityConversion';
import {
  findLineDelivery,
  getDeliveryKey,
  getDeliveryQuantity,
  getLineDeliveryKey,
  getWZLineSourceMode,
  patchWZLineSourceDelivery,
  patchWZLineSourceMode,
  validateWZLineSource,
  type WZLineSourceFields,
  type WZLineSourceMode,
  type WZSourceDelivery,
  type WZSourceLine,
} from './wzLineSource';

const { Text } = Typography;

export interface WZLineSourcePickerProps {
  line: WZSourceLine & { productName?: string };
  /** Candidate deliveries of this line's product; undefined while not loaded. */
  deliveries: WZSourceDelivery[] | undefined;
  loading?: boolean;
  loadFailed?: boolean;
  disabled?: boolean;
  onChange: (patch: WZLineSourceFields) => void;
}

/**
 * "Skąd wydajesz towar?" for one WZ line: warehouse FIFO (default), straight
 * from one truck/PZ, or part from the truck and the rest from the warehouse.
 * The choice is saved with the WZ; the backend pins the truck goods to the
 * line (see `wzLineSource.ts`).
 */
const WZLineSourcePicker: React.FC<WZLineSourcePickerProps> = ({
  line,
  deliveries,
  loading = false,
  loadFailed = false,
  disabled = false,
  onChange,
}) => {
  const { t, i18n } = useTranslation(['wz']);
  const mode = getWZLineSourceMode(line);
  const unitLabel = formatUnit(line.unit, i18n.language);
  const hasCatalogProduct = Boolean(line.productVariantId);
  const issue = validateWZLineSource(line, loading || loadFailed ? undefined : deliveries);
  const selected = findLineDelivery(line, deliveries);

  const describeDelivery = (delivery: WZSourceDelivery) => {
    const received = getDeliveryQuantity(delivery.receivedAvailable, line.unit);
    const inTransit = getDeliveryQuantity(delivery.inTransit, line.unit);
    const name = delivery.label
      || (delivery.kind === 'PZ' ? t('wz:source.unnamedPZ') : t('wz:source.unnamedTruck'));
    const parts = [
      delivery.kind === 'PZ' ? t('wz:source.pzLabel', { number: name }) : name,
      delivery.supplierNames.join(', '),
      received ? t('wz:source.receivedQuantity', { quantity: formatWarehouseQuantity(received, line.unit || 'pcs', i18n.language) }) : null,
      inTransit ? t('wz:source.inTransitQuantity', { quantity: formatWarehouseQuantity(inTransit, line.unit || 'pcs', i18n.language) }) : null,
    ].filter(Boolean);
    return parts.join(' · ');
  };

  const handleModeChange = (next: WZLineSourceMode) => {
    const patch = patchWZLineSourceMode(line, next);
    // A single candidate is the obvious choice; preselect it.
    if (next !== 'FIFO' && !getLineDeliveryKey(line) && deliveries?.length === 1) {
      Object.assign(patch, patchWZLineSourceDelivery(deliveries[0]));
    }
    onChange(patch);
  };

  const handleDeliveryChange = (key: string | undefined) => {
    const delivery = (deliveries || []).find((candidate) => getDeliveryKey(candidate) === key) || null;
    onChange(patchWZLineSourceDelivery(delivery));
  };

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }} data-testid="wz-line-source-picker">
      <Radio.Group
        value={mode}
        disabled={disabled}
        onChange={(event) => handleModeChange(event.target.value as WZLineSourceMode)}
      >
        <Space direction="vertical" size={4}>
          <Radio value="FIFO">{t('wz:source.modes.fifo')}</Radio>
          <Radio value="TRUCK" disabled={!hasCatalogProduct}>{t('wz:source.modes.truck')}</Radio>
          <Radio value="MIX" disabled={!hasCatalogProduct}>{t('wz:source.modes.mix')}</Radio>
        </Space>
      </Radio.Group>
      {!hasCatalogProduct && (
        <Text type="secondary">{t('wz:source.catalogProductHint')}</Text>
      )}
      {mode !== 'FIFO' && (
        <>
          <Select
            data-testid="wz-line-source-delivery"
            value={getLineDeliveryKey(line) || undefined}
            loading={loading}
            disabled={disabled || loading}
            placeholder={t('wz:source.deliveryPlaceholder')}
            aria-label={t('wz:source.deliveryLabel')}
            onChange={handleDeliveryChange}
            style={{ width: '100%' }}
            notFoundContent={loadFailed ? t('wz:source.loadFailed') : t('wz:source.noDeliveries')}
            options={(deliveries || []).map((delivery) => ({
              value: getDeliveryKey(delivery),
              label: describeDelivery(delivery),
            }))}
          />
          {mode === 'MIX' && (
            <InputNumber
              data-testid="wz-line-source-quantity"
              step={1}
              precision={3}
              disabled={disabled}
              value={line.sourceQuantity ?? null}
              onChange={(value) => onChange({ sourceQuantity: value === null ? null : Number(value) })}
              addonBefore={t('wz:source.truckQuantityLabel')}
              addonAfter={unitLabel}
              style={{ width: '100%' }}
            />
          )}
          {selected && getDeliveryQuantity(selected.inTransit, line.unit) ? (
            <Text type="secondary">{t('wz:source.inTransitHint')}</Text>
          ) : null}
        </>
      )}
      {issue && (
        <Text type="danger" data-testid="wz-line-source-issue">{t(issue.key, {
          ...issue.values,
          ...(issue.values?.available !== undefined
            ? { available: formatWarehouseQuantity(Number(issue.values.available), line.unit || 'pcs', i18n.language) }
            : {}),
        })}</Text>
      )}
    </Space>
  );
};

export default WZLineSourcePicker;
